import { parseTimezoneAwareDatetime, getUserTimezone } from './timezone';

export const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const days2 = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const withZero = (num: number)=>{
    if (Number.isNaN(num)){
        return '--'
    }
    return num < 10 ? '0' + num : '' + num;
}

// July 2024
export const formatDateMonth = (date: Date) => {
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

export const formatDateMonthLocalized = (date: Date, translate: (key: string) => string) => {
    const localizedMonthNames = getLocalizedMonthNames(translate);
    return `${localizedMonthNames[date.getMonth()]} ${date.getFullYear()}`;
};

// 24 July 2024
export const formatDateDayMonth = (date: Date)=>{
    if (!date){
        return ''
    }
    return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;

}

//'WED 12:24' (12-hour for English, 24-hour for Russian/Ukrainian) with timezone conversion
export const formatDate1 = (date: Date, translate?: (key: string) => string, language?: string) => {
    if (!date){
        return '';
    }
    if (Number.isNaN(date.valueOf())){
        return 'DAY --:--';
    }

    try {
        // Use Intl.DateTimeFormat for proper timezone conversion
        const timezone = getUserTimezone();
        
        // Get day name using proper timezone formatting
        const dayFormatter = new Intl.DateTimeFormat('en-US', { 
            weekday: 'short', 
            timeZone: timezone 
        });
        const dayName = dayFormatter.format(date).toUpperCase();

        // If translation function is provided, use localized short day names
        let finalDayName = dayName;
        if (translate) {
            // Use formatToParts to safely get the day index in the target timezone
            const dayParts = new Intl.DateTimeFormat('en-US', { 
                weekday: 'short',
                timeZone: timezone 
            }).formatToParts(date);
            
            const weekdayPart = dayParts.find(part => part.type === 'weekday');
            if (weekdayPart) {
                // Map from day names to indices consistently
                const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dayIndex = dayNames.findIndex(name => name === weekdayPart.value);
                if (dayIndex !== -1) {
                    const localizedShortDays = getLocalizedShortDays(translate);
                    finalDayName = localizedShortDays[dayIndex].toUpperCase();
                }
            }
        }

        // Format time based on language - 24-hour for Russian/Ukrainian, 12-hour for English
        const use24Hour = language === 'ru' || language === 'ua';
        
        // Get time using proper timezone formatting
        const timeFormatter = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: !use24Hour,
            timeZone: timezone
        });
        
        const timeString = timeFormatter.format(date);
        
        return `${finalDayName}  ${timeString}`;
    } catch (e) {
        console.warn('Failed to format date with timezone conversion:', e);
        // Fallback to UTC formatting
        let dayName = days[date.getUTCDay()].toUpperCase();
        if (translate) {
            const localizedShortDays = getLocalizedShortDays(translate);
            dayName = localizedShortDays[date.getUTCDay()].toUpperCase();
        }
        const use24Hour = language === 'ru' || language === 'ua';
        if (use24Hour) {
            return `${dayName}  ${withZero(date.getUTCHours())}:${withZero(date.getUTCMinutes())}`;
        } else {
            const hours24 = date.getUTCHours();
            const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
            const ampm = hours24 >= 12 ? 'PM' : 'AM';
            return `${dayName}  ${hours12}:${withZero(date.getUTCMinutes())} ${ampm}`;
        }
    }
}

/**
 * Compact version of formatDate1 for client lists using 24-hour format to prevent wrapping
 * @param date - Date object to format
 * @param translate - Translation function
 * @param language - Language (not used, always 24-hour)
 * @returns Compact date with day and time in 24-hour format
 */
export const formatDate1Compact = (date: Date, translate?: (key: string) => string, language?: string) => {
    if (!date){
        return '';
    }
    if (Number.isNaN(date.valueOf())){
        return 'DAY --:--';
    }

    try {
        // Use Intl.DateTimeFormat for proper timezone conversion
        const timezone = getUserTimezone();
        
        // Get day name using proper timezone formatting
        const dayFormatter = new Intl.DateTimeFormat('en-US', { 
            weekday: 'short', 
            timeZone: timezone 
        });
        const dayName = dayFormatter.format(date).toUpperCase();

        // If translation function is provided, use localized short day names
        let finalDayName = dayName;
        if (translate) {
            // Use formatToParts to safely get the day index in the target timezone
            const dayParts = new Intl.DateTimeFormat('en-US', { 
                weekday: 'short',
                timeZone: timezone 
            }).formatToParts(date);
            
            const weekdayPart = dayParts.find(part => part.type === 'weekday');
            if (weekdayPart) {
                // Map from day names to indices consistently
                const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dayIndex = dayNames.findIndex(name => name === weekdayPart.value);
                if (dayIndex !== -1) {
                    const localizedShortDays = getLocalizedShortDays(translate);
                    finalDayName = localizedShortDays[dayIndex].toUpperCase();
                }
            }
        }

        // Always use 24-hour format for compact display
        const timeFormatter = new Intl.DateTimeFormat('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false, // Always 24-hour format
            timeZone: timezone
        });
        
        const timeString = timeFormatter.format(date);
        
        return `${finalDayName}  ${timeString}`;
    } catch (e) {
        console.warn('Failed to format date with timezone conversion:', e);
        // Fallback to UTC formatting with 24-hour format
        let dayName = days[date.getUTCDay()].toUpperCase();
        if (translate) {
            const localizedShortDays = getLocalizedShortDays(translate);
            dayName = localizedShortDays[date.getUTCDay()].toUpperCase();
        }
        return `${dayName}  ${withZero(date.getUTCHours())}:${withZero(date.getUTCMinutes())}`;
    }
}

//'WEDNESDAY 12:24' - for session items using user's timezone
export const formatDate1Session = (date: Date, translate?: (key: string) => string) =>{
    if (!date){
        return '';
    }

    try {
        const timezone = getUserTimezone();
        
        if (translate) {
            const dayKeys = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];
            
            // Use Intl.DateTimeFormat to safely get components in user's timezone
            const dayParts = new Intl.DateTimeFormat('en-US', {
                weekday: 'short',
                day: 'numeric', 
                month: 'numeric',
                timeZone: timezone
            }).formatToParts(date);
            
            const weekdayPart = dayParts.find(part => part.type === 'weekday');
            const dayPart = dayParts.find(part => part.type === 'day');
            const monthPart = dayParts.find(part => part.type === 'month');
            
            if (weekdayPart && dayPart && monthPart) {
                const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dayIndex = dayNames.findIndex(name => name === weekdayPart.value);
                if (dayIndex !== -1) {
                    const dayName = translate(dayKeys[dayIndex]);
                    const day = parseInt(dayPart.value);
                    const month = parseInt(monthPart.value);
                    return `${dayName}, ${day}.${month < 10 ? '0' + month : month}`;
                }
            }
        } else {
            // Use Intl.DateTimeFormat to safely get components in user's timezone
            const dayParts = new Intl.DateTimeFormat('en-US', {
                weekday: 'short',
                day: 'numeric', 
                month: 'numeric',
                timeZone: timezone
            }).formatToParts(date);
            
            const weekdayPart = dayParts.find(part => part.type === 'weekday');
            const dayPart = dayParts.find(part => part.type === 'day');
            const monthPart = dayParts.find(part => part.type === 'month');
            
            if (weekdayPart && dayPart && monthPart) {
                const dayNameString = weekdayPart.value;
                const day = parseInt(dayPart.value);
                const month = parseInt(monthPart.value);
                return `${dayNameString}, ${day}.${month < 10 ? '0' + month : month}`;
            }
        }
    } catch (e) {
        console.warn('Failed to format session date with timezone:', e);
        // Fallback to UTC
        if (translate) {
            const dayKeys = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];
            const dayName = translate(dayKeys[date.getUTCDay()]);
            const day = date.getUTCDate();
            const month = date.getUTCMonth() + 1;
            return `${dayName}, ${day}.${month < 10 ? '0' + month : month}`;
        } else {
            const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()];
            const day = date.getUTCDate();
            const month = date.getUTCMonth() + 1;
            return `${dayName}, ${day}.${month < 10 ? '0' + month : month}`;
        }
    }
}

//'Friday, 27 July' - with timezone awareness
export const formatDate2 = (date: Date, translate?: (key: string) => string) =>{
    if (!date){
        return '';
    }

    try {
        const timezone = getUserTimezone();
        
        if (translate) {
            const dayKeys = ['day_sunday', 'day_monday', 'day_tuesday', 'day_wednesday', 'day_thursday', 'day_friday', 'day_saturday'];
            const monthKeys = ['month_january', 'month_february', 'month_march', 'month_april', 'month_may', 'month_june', 
                              'month_july', 'month_august', 'month_september', 'month_october', 'month_november', 'month_december'];

            // Use Intl.DateTimeFormat to safely get components in user's timezone
            const dateParts = new Intl.DateTimeFormat('en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                timeZone: timezone
            }).formatToParts(date);
            
            const weekdayPart = dateParts.find(part => part.type === 'weekday');
            const dayPart = dateParts.find(part => part.type === 'day');
            const monthPart = dateParts.find(part => part.type === 'month');
            
            if (weekdayPart && dayPart && monthPart) {
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                
                const dayIndex = dayNames.findIndex(name => name === weekdayPart.value);
                const monthIndex = monthNames.findIndex(name => name === monthPart.value);
                
                if (dayIndex !== -1 && monthIndex !== -1) {
                    const dayName = translate(dayKeys[dayIndex]);
                    const monthName = translate(monthKeys[monthIndex]);
                    const day = parseInt(dayPart.value);
                    return `${dayName}, ${day} ${monthName}`;
                }
            }
        } else {
            // Use Intl.DateTimeFormat to safely get components in user's timezone  
            const dateParts = new Intl.DateTimeFormat('en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                timeZone: timezone
            }).formatToParts(date);
            
            const weekdayPart = dateParts.find(part => part.type === 'weekday');
            const dayPart = dateParts.find(part => part.type === 'day');
            const monthPart = dateParts.find(part => part.type === 'month');
            
            if (weekdayPart && dayPart && monthPart) {
                const dayName = weekdayPart.value;
                const monthName = monthPart.value;
                const day = parseInt(dayPart.value);
                return `${dayName}, ${day} ${monthName}`;
            }
        }
    } catch (e) {
        console.warn('Failed to format date2 with timezone:', e);
        // Fallback to UTC
        if (translate) {
            const dayKeys = ['day_sunday', 'day_monday', 'day_tuesday', 'day_wednesday', 'day_thursday', 'day_friday', 'day_saturday'];
            const monthKeys = ['month_january', 'month_february', 'month_march', 'month_april', 'month_may', 'month_june', 
                              'month_july', 'month_august', 'month_september', 'month_october', 'month_november', 'month_december'];

            const dayName = translate(dayKeys[date.getUTCDay()]);
            const monthName = translate(monthKeys[date.getUTCMonth()]);
            const day = date.getUTCDate();
            return `${dayName}, ${day} ${monthName}`;
        } else {
            const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getUTCDay()];
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const monthName = months[date.getUTCMonth()];
            const day = date.getUTCDate();
            return `${dayName}, ${day} ${monthName}`;
        }
    }
}

//dd.mm with timezone awareness
export const formatDate3 = (date: Date)=>{
    if (!date || Number.isNaN(date.valueOf())){
        return '--.--.--'
    }
    
    try {
        const timezone = getUserTimezone();
        
        // Use Intl.DateTimeFormat to safely get components in user's timezone
        const dateParts = new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'numeric',
            timeZone: timezone
        }).formatToParts(date);
        
        const dayPart = dateParts.find(part => part.type === 'day');
        const monthPart = dateParts.find(part => part.type === 'month');
        
        if (dayPart && monthPart) {
            const day = parseInt(dayPart.value);
            const month = parseInt(monthPart.value);
            return withZero(day) + '.' + withZero(month);
        }
        
        // Fallback to UTC if formatToParts fails
        return withZero(date.getUTCDate()) + '.' + withZero(date.getUTCMonth() + 1);
    } catch (e) {
        console.warn('Failed to format date3 with timezone:', e);
        // Fallback to UTC
        return withZero(date.getUTCDate()) + '.' + withZero(date.getUTCMonth() + 1);
    }
}

// Convert Unix timestamp or ISO string to proper Date object
export const timestampToDate = (timestamp: number | string): Date => {
    if (!timestamp) return new Date(0);

    // If it's a string, check if it's an ISO date string first
    if (typeof timestamp === 'string') {
        // Check if it looks like an ISO date string (contains 'T' and 'Z' or timezone)
        if (timestamp.includes('T') && (timestamp.includes('Z') || timestamp.includes('+') || timestamp.includes('-'))) {
            return new Date(timestamp);
        }
        // Otherwise, treat as numeric timestamp
        const numericTimestamp = parseFloat(timestamp);
        const timestampMs = numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp;
        return new Date(timestampMs);
    }

    // If timestamp is in seconds (less than 1e12), convert to milliseconds
    const timestampMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;

    return new Date(timestampMs);
}

//'2013-01-08' 
export const formatForInputValue = (date: Date) => {
    if (!date){
        return ''
    }
    return `${date.getFullYear()}-${withZero(date.getMonth()+1)}-${withZero(date.getDate())}`;
}

//'15:08' using local timezone
export const formatTimeForInputValue = (date: Date) => {
    if (!date){
        return ''
    }
    return `${withZero(date.getHours())}:${withZero(date.getMinutes())}`;
}

export const formatMinutes = (date: Date) => {
    if (!date){
        return ''
    }
    return `${withZero(date.getUTCMinutes())}:${withZero(date.getUTCSeconds())}`;
}

export const timeFromSeconds = (seconds:number) => {
    return formatMinutes(new Date(Math.floor(seconds * 1000)));
}

export const formatFileName = (name: string)=>{
    if (typeof name != 'string'){
        return '';
    }
    let dotPos = name.split('').lastIndexOf('.');
    if (dotPos == -1){
        dotPos = name.length-1;
    }
    const firstPart = name.slice(0, dotPos);
    const secondPart =  name.slice(dotPos);
    const shortPart = firstPart.length < 15 ? firstPart : firstPart.slice(0, 12)+'...';
    const secondPartShort = secondPart.length < 9  ? secondPart : secondPart.slice(secondPart.length-9);
    return shortPart+secondPartShort;
}

export const fixHTMLSpaces = (name: string)=>{
    return name.split('  ').join('\u00A0\u00A0');
}

console.log('fname', formatFileName('dfghajefasdf sddas das fad fdas a.gsfg.dfgjh'));
console.log('fname', formatFileName('simple name.dtf'))
console.log('fname', formatFileName('nodot'))
console.log('fname', formatFileName('dfghajefasdf sddas das fad fdas afdfsfdsdsjkhgj'))
console.log('fname', formatFileName('dfghajefasdf sddas .das fad fdas afdfsfdsdsjkhgj'))
//'24 Aug'

export function getLocalizedMonthNames(translate: (key: string) => string): string[] {
    return [
        translate("month_january"),
        translate("month_february"),
        translate("month_march"),
        translate("month_april"),
        translate("month_may"),
        translate("month_june"),
        translate("month_july"),
        translate("month_august"),
        translate("month_september"),
        translate("month_october"),
        translate("month_november"),
        translate("month_december")
    ];
}

export function getLocalizedDays(translate: (key: string) => string): string[] {
    return [
        translate('day_sunday'),
        translate('day_monday'),
        translate('day_tuesday'),
        translate('day_wednesday'),
        translate('day_thursday'),
        translate('day_friday'),
        translate('day_saturday')
    ];
}

export function getLocalizedShortDays(translate: (key: string) => string): string[] {
    return [
        translate('day_sun'),
        translate('day_mon'),
        translate('day_tue'),
        translate('day_wed'),
        translate('day_thu'),
        translate('day_fri'),
        translate('day_sat')
    ];
}

// Timezone-aware helper functions for calendar operations
// Using native Date methods with proper timezone offset calculation
export const getTimezoneAwareMinutes = (date: Date): number => {
    return date.getMinutes();
};

export const getTimezoneAwareHours = (date: Date): number => {
    return date.getHours();
};

export const getTimezoneAwareDate = (date: Date): number => {
    return date.getDate();
};

export const getTimezoneAwareMonth = (date: Date): number => {
    return date.getMonth();
};

export const getTimezoneAwareYear = (date: Date): number => {
    return date.getFullYear();
};

export const getTimezoneAwareDay = (date: Date): number => {
    return date.getDay();
};

// Set time in user's timezone (returns new Date object)
export const setTimezoneAwareHours = (date: Date, hours: number, minutes: number = 0, seconds: number = 0, ms: number = 0): Date => {
    const newDate = new Date(date);
    newDate.setHours(hours, minutes, seconds, ms);
    return newDate;
};